import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useJobRunsQuery, useTriggerJobMutation } from '@/hooks/useJobs';
import { ApiError } from '@/api/client';
import type { JobSummaryDto, JobRunDto, JobSlug, JobRunStatus, JobName } from '@/api/types';

export const JOB_SLUGS: Record<JobName, JobSlug> = {
  OuCatalogSync: 'ou-catalog-sync',
  UserCatalogSync: 'user-catalog-sync',
  SuccessFactorsUserSync: 'sf-user-sync',
  SuccessFactorsOuSync: 'sf-ou-sync',
  AdDomainSweep: 'ad-domain-sweep',
};

/**
 * 🔴 Las fechas y los números se formatean con el idioma de la APLICACIÓN, no con el del navegador.
 * `toLocaleString()` sin argumento usa el del navegador: una persona con la aplicación en inglés y
 * Windows en español veía las etiquetas en inglés y las fechas en español, en la misma fila. Era
 * parte de la mezcla de idiomas que se reportó.
 */
export function fmtDate(iso: string, idioma: string): string {
  try { return new Date(iso).toLocaleString(idioma); }
  catch { return iso; }
}

export function fmtDuration(durationMs: number, idioma: string): string {
  return durationMs < 1000
    ? `${durationMs.toLocaleString(idioma)} ms`
    : `${(durationMs / 1000).toLocaleString(idioma, { maximumFractionDigits: 1 })} s`;
}

/**
 * Los cinco contadores del BARRIDO DE DOMINIOS (REQ 2 / REQ 5).
 */
function SweepCounters({ run }: { run: JobRunDto }) {
  const { t } = useTranslation();
  const n = (v: number | null | undefined) => v ?? 0;

  return (
    <>
      <div className="jobs-card-row">
        <span>{t('jobs.cuentasBarridas')}</span>
        <strong>{run.cuentasBarridas ?? '—'}</strong>
      </div>
      <div className="jobs-card-row">
        <span>{t('jobs.dominiosObservados')}</span>
        <strong>{run.dominiosObservados ?? '—'}</strong>
      </div>
      <div className="jobs-card-row">
        <span>{t('jobs.dominiosNoDeclarados')}</span>
        <strong>{run.dominiosNoDeclarados ?? '—'}</strong>
      </div>
      {/* 🔴 Este SIEMPRE se pinta, y por la misma razón que `ouHuerfanaSinResolver`: su estado normal
          es cero, así que esconderlo cuando vale 0 haría que la fila ausente significara tres cosas a
          la vez — "ninguno desapareció", "esta corrida no traía el dato" y "el backend no lo mandó".
          Y aquí el precio de confundirlas es alto: si alguien lee esta lista y saca el dominio del
          catálogo, ABRE el auto-registro a toda su gente. */}
      <div className="jobs-card-row">
        <span>{t('jobs.dominiosDesaparecidos')}</span>
        <strong className={n(run.dominiosDesaparecidos) > 0 ? 'jobs-bad' : 'jobs-ok'}>
          {run.dominiosDesaparecidos ?? '—'}
        </strong>
      </div>
      {n(run.dominiosDesactivados) > 0 && (
        <div className="jobs-card-row">
          <span>{t('jobs.dominiosDesactivados')}</span>
          <strong>{run.dominiosDesactivados}</strong>
        </div>
      )}
    </>
  );
}

/**
 * Los contadores del sync, con la identidad ya resuelta.
 *
 * 🔴 SIN namespace en `useTranslation()`. Las claves `jobs.*` viven en el ns por defecto
 * (`translation`); pedir `recognition` —donde no hay ni una clave `jobs`— devolvía la clave cruda en
 * pantalla. Pasó de verdad: QA llevaba viendo `jobs.identity` y `jobs.drift` literales.
 */
function SyncCounters({ run }: { run: JobRunDto }) {
  const { t } = useTranslation();
  const n = (v: number | null | undefined) => v ?? 0;

  const suma = n(run.duplicateInFeed) + n(run.sinEmpleo) + n(run.skippedByMode) + n(run.ignored)
             + n(run.matchConflict) + n(run.created) + n(run.updated) + n(run.unchanged);
  const cuadra = suma === n(run.received);

  // La desviación es lo que detecta que la paginación perdió gente. Tolerancia 0.5 %.
  const esperado = n(run.expectedCount);
  const desviacion = esperado > 0 ? Math.abs(n(run.distinctSeen) - esperado) / esperado : 0;

  return (
    <>
      <div className="jobs-card-row">
        <span>{t('jobs.identity')}</span>
        <strong className={cuadra ? 'jobs-ok' : 'jobs-bad'}>
          {cuadra ? t('jobs.identityOk') : t('jobs.identityBad', { suma, recibidas: n(run.received) })}
        </strong>
      </div>
      <div className="jobs-card-row">
        <span>{t('jobs.drift')}</span>
        <strong className={desviacion <= 0.005 ? 'jobs-ok' : 'jobs-bad'}>
          {(desviacion * 100).toFixed(3)} %
        </strong>
      </div>
      {n(run.duplicateEmail) > 0 && (
        <div className="jobs-card-row">
          <span>{t('jobs.duplicateEmail')}</span>
          <strong>{run.duplicateEmail}</strong>
        </div>
      )}
      {n(run.managerLinksUpdated) > 0 && (
        <div className="jobs-card-row">
          <span>{t('jobs.managerLinks')}</span>
          <strong>{run.managerLinksUpdated}</strong>
        </div>
      )}
      {n(run.ouSubidaARaiz) > 0 && (
        <div className="jobs-card-row">
          <span>{t('jobs.ouSubidaARaiz')}</span>
          <strong>{run.ouSubidaARaiz}</strong>
        </div>
      )}
      {n(run.ouHuerfanaResuelta) > 0 && (
        <div className="jobs-card-row">
          <span>{t('jobs.ouHuerfanaResuelta')}</span>
          <strong>{run.ouHuerfanaResuelta}</strong>
        </div>
      )}
      {/* 🔴 Este SIEMPRE se pinta, y es la diferencia entre una alarma y un adorno. Los otros dos son
          reparaciones: si valen 0 no hay nada que contar. Este es lo que quedó ROTO, y su estado
          normal es cero — o sea que si sólo se pintara cuando es > 0, la fila ausente significaría a la
          vez "no quedó ninguna", "esta corrida es vieja y no traía el dato" y "el backend no lo
          mandó". Tres cosas distintas con el mismo aspecto: exactamente como no se ve un fallo. */}
      {run.received != null && (
        <div className="jobs-card-row">
          <span>{t('jobs.ouHuerfanaSinResolver')}</span>
          <strong className={n(run.ouHuerfanaSinResolver) > 0 ? 'jobs-bad' : 'jobs-ok'}>
            {run.ouHuerfanaSinResolver ?? '—'}
          </strong>
        </div>
      )}
    </>
  );
}

/**
 * Estado de una corrida.
 *
 * Reutiliza `.adm-status-badge`, la píldora del resto del panel de administración: misma geometría y
 * mismo peso tipográfico. Sólo el color es propio, porque los cinco estados de un job (nunca
 * ejecutado / corriendo / éxito / saltado / error) no empatan con los cuatro de una encuesta.
 */
function StatusBadge({ status, isRunning }: { status: JobRunStatus | null; isRunning: boolean }) {
  const { t } = useTranslation();

  if (isRunning) return <span className="adm-status-badge jobs-badge--running">{t('jobs.statusRunning')}</span>;
  if (status === null) return <span className="adm-status-badge jobs-badge--never">{t('jobs.statusNeverRun')}</span>;
  if (status === 'Success') return <span className="adm-status-badge jobs-badge--success">{t('jobs.statusSuccess')}</span>;
  if (status === 'Skipped') return <span className="adm-status-badge jobs-badge--skipped">{t('jobs.statusSkipped')}</span>;
  return <span className="adm-status-badge jobs-badge--failed">{t('jobs.statusFailed')}</span>;
}

function JobHistoryTable({ jobSlug, expanded }: { jobSlug: JobSlug; expanded: boolean }) {
  const { t, i18n } = useTranslation();
  const { data: runs, isLoading, isError } = useJobRunsQuery(jobSlug, { enabled: expanded });

  if (!expanded) return null;
  if (isLoading) return <p className="jobs-card-meta">{t('jobs.historyLoading')}</p>;
  if (isError) return <p className="jobs-alert jobs-alert--bad">{t('jobs.historyError')}</p>;
  if (!runs || runs.length === 0) return <p className="jobs-card-meta">{t('jobs.historyEmpty')}</p>;

  return (
    <div className="jobs-history-wrap">
      <table className="adm-table jobs-history-table">
        <thead>
          <tr>
            <th>{t('jobs.colDate')}</th>
            <th>{t('jobs.colTrigger')}</th>
            <th>{t('jobs.colStatus')}</th>
            <th>{t('jobs.colDuration')}</th>
            <th>{t('jobs.colDetail')}</th>
          </tr>
        </thead>
        <tbody>
          {runs.map((run: JobRunDto) => (
            <tr key={run.id}>
              <td>{fmtDate(run.startedAt, i18n.language)}</td>
              <td>{run.triggerType === 'Manual' ? t('jobs.triggerManual') : t('jobs.triggerScheduled')}</td>
              <td><StatusBadge status={run.status} isRunning={false} /></td>
              <td>{fmtDuration(run.durationMs, i18n.language)}</td>
              <td>{run.errorMessage ?? run.skipReason ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function JobCard({ job }: { job: JobSummaryDto }) {
  const { t, i18n } = useTranslation();
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const triggerJob = useTriggerJobMutation();
  const jobSlug = JOB_SLUGS[job.jobName];
  const idioma = i18n.language;

  const LABELS: Record<JobName, string> = {
    OuCatalogSync: t('jobs.ouCatalogSync'),
    UserCatalogSync: t('jobs.userCatalogSync'),
    SuccessFactorsUserSync: t('jobs.sfUserSync'),
    SuccessFactorsOuSync: t('jobs.sfOuSync'),
    AdDomainSweep: t('jobs.adDomainSweep'),
  };

  return (
    <section className="glass jobs-card">
      <header className="jobs-card-header">
        <span className="jobs-card-name">{LABELS[job.jobName] ?? job.jobName}</span>
        <StatusBadge status={job.lastRun?.status ?? null} isRunning={job.isRunning} />
      </header>

      {job.lastRun && (
        <>
          <div className="jobs-card-row">
            <span>{t('jobs.lastRun')}</span>
            <strong>
              {fmtDate(job.lastRun.startedAt, idioma)} ({fmtDuration(job.lastRun.durationMs, idioma)})
            </strong>
          </div>
          {job.lastRun.status === 'Success' && (
            <div className="jobs-card-row">
              <span>{t('jobs.created')} / {t('jobs.updated')} / {t('jobs.unchanged')}</span>
              <strong>{job.lastRun.created} / {job.lastRun.updated} / {job.lastRun.unchanged}</strong>
            </div>
          )}
          {job.lastRun.status === 'Success' && job.lastRun.received != null && (
            <SyncCounters run={job.lastRun} />
          )}
          {/* Se decide por el JOB y no por "el contador no es null": un barrido que abortara con los
              cinco en null seguiría siendo un barrido, y el bloque tiene que salir para decir que no
              trajo cifras. Con la condición al revés, la tarjeta se quedaría muda justo cuando importa. */}
          {job.jobName === 'AdDomainSweep' && job.lastRun.status === 'Success' && (
            <SweepCounters run={job.lastRun} />
          )}
          {job.lastRun.status === 'Skipped' && (
            <div className="jobs-card-row">
              <span>{t('jobs.skipReason')}</span>
              <strong>{job.lastRun.skipReason}</strong>
            </div>
          )}
          {job.lastRun.status === 'Failed' && (
            <p className="jobs-alert jobs-alert--bad">{job.lastRun.errorMessage}</p>
          )}
        </>
      )}

      <div className="jobs-card-row">
        <span>{t('jobs.nextScheduled')}</span>
        <strong>{job.nextScheduledAt ? fmtDate(job.nextScheduledAt, idioma) : t('jobs.nextScheduledUnknown')}</strong>
      </div>

      <div className="jobs-card-actions">
        <button
          className="btn btn--primary btn--sm"
          type="button"
          disabled={job.isRunning || triggerJob.isPending}
          title={job.isRunning ? t('jobs.runningDisabled') : undefined}
          onClick={() => triggerJob.mutate(jobSlug)}
        >
          {t('jobs.runNow')}
        </button>
        <button
          className="btn btn--link btn--sm"
          type="button"
          onClick={() => setHistoryExpanded(current => !current)}
        >
          {historyExpanded ? t('jobs.hideHistory') : t('jobs.viewHistory')}
        </button>
      </div>

      {triggerJob.isError && (
        <p className="jobs-alert jobs-alert--bad" role="alert">
          {triggerJob.error instanceof ApiError && triggerJob.error.status === 409
            ? t('jobs.triggerConflict')
            : t('jobs.triggerError')}
        </p>
      )}

      <JobHistoryTable jobSlug={jobSlug} expanded={historyExpanded} />
    </section>
  );
}
